import { Daphne } from "@syadem/daphne-js";
import { Professional, Team } from "@syadem/ariane-js";
import {
  VaccinationAct,
  VaccinationActCertificationMethodEnum,
  VaccinationActCertificator,
  VaccinationActCertificatorTypeEnum,
} from "@syadem/kairos-citizen-js";
import { LocaleString } from "../translations";
import { getTranslatedFieldByLocale } from "./translatedFields";
import { I18nInterface } from "../ui/providers/I18nContext";

const printableDiseasesGroups = [
  [
    1, // "Diphtérie"
    2, // "Tétanos"
    4  // "Poliomyélite"
  ],
  [
    1, // "Diphtérie"
    2, // "Tétanos"
    3, // "Coqueluche"
    4  // "Poliomyélite"
  ],
  [
    1, // "Diphtérie"
    2, // "Tétanos"
    3  // "Coqueluche"
  ],
  [
    1, // "Diphtérie"
    2, // "Tétanos"
    3, // "Coqueluche"
    4, // "Poliomyélite"
    5  // "Haemophilus influenzae b"
  ],
  [
    1, // "Diphtérie"
    2, // "Tétanos"
    3, // "Coqueluche"
    4, // "Poliomyélite"
    5, // "Haemophilus influenzae b"
    6  // "Hépatite B"
  ],
  [
    8, // "Rougeole"
    9, // "Oreillons"
    10 // "Rubéole"
  ],
  [
    16, // "Hépatite A"
    6   // "Hépatite B"
  ],
  [
    44, // "Méningocoque A"
    43, // "Méningocoque C"
    46, // "Méningocoque W"
    47  // "Méningocoque Y"
  ],
  [
    16, // "Hépatite A"
    18  // "Typhoïde"
  ]
];

export const sortByPerformedOnDates = (a: VaccinationAct, b: VaccinationAct) => {
  if (a.performedOn < b.performedOn) {
    return 1;
  }
  if (a.performedOn > b.performedOn) {
    return -1;
  }
  return 0;
};

const sameArray = (a: number[], b: number[]) => {
  return a.length === b.length && a.every(x => b.includes(x));
}

export const groupVaccinationActsByDisease = (
  vaccinationActs: VaccinationAct[],
  daphne: Daphne,
  locale: LocaleString,
): Partial<Record<string, VaccinationAct[]>> => {
  return Object.groupBy(vaccinationActs, (vaccination) => {
    const vaccine = daphne.repositories.vaccines.find(vaccination.vaccineId);
    const diseasesCodes = daphne.queries.diseasesByVaccine(vaccine).map((d) => d.code);
    const printableGroup = printableDiseasesGroups.find((g) => sameArray(g, diseasesCodes)) ?? diseasesCodes;

    return printableGroup.map((code) => {
      const disease = daphne.repositories.diseases.all().find((d) => d.code === code);

      if (disease === undefined) return "";

      return getTranslatedFieldByLocale(locale, disease.name)
    }).join(", ");
  })
};

export function retrieveCertificator(
  vaccinationAct: VaccinationAct,
  certificators?: (Professional | Team)[],
): Professional | Team | undefined {
  if (vaccinationAct.certificator && certificators) {
    return certificators.find(
      (certificator) => certificator.id === (vaccinationAct.certificator as VaccinationActCertificator).id,
    );
  } else {
    return undefined;
  }
}

export function certificationAdditionalInfo(vaccinationAct: VaccinationAct, t: I18nInterface["t"]): string {
  if (vaccinationAct.certificationMethod === VaccinationActCertificationMethodEnum.Proof) {
    return t("vaccines.editableByAnyPro");
  } else if (vaccinationAct.certificator?.type === VaccinationActCertificatorTypeEnum.Professional) {
    return t("vaccines.editableByProOnly");
  } else if (vaccinationAct.certificator?.type === VaccinationActCertificatorTypeEnum.Team) {
    return t("vaccines.editableByTeamOnly");
  } else {
    return "";
  }
}
